import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { article } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PlusCft } from "scmp-app/components/plus/cft";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";
import type { cftInPlusPageQuery } from "scmp-app/queries/__generated__/cftInPlusPageQuery.graphql";

export const query = graphql`
  query cftInPlusPageQuery($nodeId: ID!, $hasNodeId: Boolean!) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
  }
`;

const PlusCftPage: RelayComponent<PageProps, cftInPlusPageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const pageLocation = useGetPageLocationForPlusPage();
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    if (!notEmpty(pageLocation)) {
      return;
    }
    trackPageView?.({
      page_location: pageLocation,
    });
  }, [pageLocation, trackPageView]);

  return (
    <>
      <PlusHomeSeo reference={queryReference.node} />
      <PlusMobileScreen />
      <PlusCft />
    </>
  );
};

PlusCftPage.displayName = "PlusCftPage";

const pageProps: PageProps = {
  animateKey: "plus-china-future-tech",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(PlusCftPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(_context) {
    return {
      chinaFutureTechBriefingsTypeUuid: article.types.chinaFutureTechBriefing.entityUuid,
      hasNodeId: false,
      nodeId: "",
    };
  },
});
